import { type KeyboardEvent, useRef } from 'react'
import { cn } from '@/shared/lib'
import { Chip } from '@/shared/ui/chip'
import { Icon } from '@/shared/ui/icon'

interface TagInputProps {
  id?: string
  value: string[]
  onChange: (next: string[]) => void
  /** Текст в инпуте — контролируется родителем (он же грузит подсказки по нему). */
  query: string
  onQueryChange: (query: string) => void
  suggestions?: string[]
  placeholder?: string
  maxTags?: number
  disabled?: boolean
  'aria-invalid'?: boolean
  'aria-describedby'?: string
}

function normalize(tag: string) {
  return tag.trim().toLowerCase()
}

// Dumb-редактор тегов: выбранные — чипы с удалением внутри поля, доступные —
// чипы под ним (кликом добавляются). Enter/запятая добавляет введённое,
// Backspace на пустом инпуте снимает последний тег.
// Данные (value/query/suggestions) полностью у родителя — компонент без побочек.
export function TagInput({
  id,
  value,
  onChange,
  query,
  onQueryChange,
  suggestions = [],
  placeholder,
  maxTags,
  disabled,
  ...aria
}: TagInputProps) {
  const inputRef = useRef<HTMLInputElement>(null)

  const atLimit = maxTags !== undefined && value.length >= maxTags
  const visible = suggestions
    .filter((s) => !value.some((t) => normalize(t) === normalize(s)))
    .slice(0, 8)
  // Не завязано на фокус: чипы под полем — часть раскладки, а не всплывающий
  // список; мигание по фокусу дёргало бы высоту формы.
  const showSuggestions = !atLimit && visible.length > 0

  function add(raw: string) {
    const tag = raw.trim()
    if (!tag || atLimit) return
    if (value.some((t) => normalize(t) === normalize(tag))) {
      onQueryChange('')
      return
    }
    onChange([...value, tag])
    onQueryChange('')
  }

  function remove(index: number) {
    onChange(value.filter((_, i) => i !== index))
  }

  function handleKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' || e.key === ',') {
      // Enter внутри формы не должен сабмитить, пока пользователь набирает тег.
      if (query.trim() !== '' || e.key === ',') e.preventDefault()
      add(query)
      return
    }
    if (e.key === 'Backspace' && query === '' && value.length > 0) {
      remove(value.length - 1)
    }
  }

  return (
    <div>
      {/* label-обёртка: клик по любому месту контрола фокусирует инпут нативно */}
      <label
        className={cn(
          'flex w-full cursor-text flex-wrap items-center gap-2 rounded-control border border-border-default bg-bg-sunken px-4 py-3 transition-colors focus-within:border-border-accent',
          disabled && 'opacity-50',
        )}
      >
        {value.map((tag, i) => (
          <Chip key={tag} tone="info" className="normal-case">
            {tag}
            <button
              type="button"
              aria-label={`Убрать тег ${tag}`}
              className="text-text-tertiary transition-colors hover:text-text-primary"
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation()
                remove(i)
              }}
            >
              <Icon name="close" size={12} />
            </button>
          </Chip>
        ))}
        <input
          ref={inputRef}
          id={id}
          {...aria}
          className="min-w-28 flex-1 bg-transparent text-text-primary placeholder:text-text-tertiary focus:outline-none"
          value={query}
          disabled={disabled || atLimit}
          placeholder={value.length === 0 ? placeholder : undefined}
          onChange={(e) => onQueryChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => {
            // Набранный, но не подтверждённый Enter'ом текст — тоже тег: иначе
            // «ввёл и сразу нажал Создать» молча теряет тег.
            add(query)
          }}
        />
      </label>
      {showSuggestions && (
        <div className="mt-2 flex flex-wrap gap-2">
          {visible.map((s) => (
            <button
              key={s}
              type="button"
              className="rounded-control focus:outline-none focus-visible:ring-1 focus-visible:ring-border-accent"
              disabled={disabled}
              // mousedown раньше blur инпута: иначе blur успевает добавить
              // набранный текст и список подсказок перестраивается под курсором.
              onMouseDown={(e) => {
                e.preventDefault()
                add(s)
              }}
            >
              <Chip
                variant="soft"
                className="gap-1 text-text-tertiary transition-colors hover:bg-bg-surface hover:text-text-primary"
              >
                <Icon name="plus" size={12} />
                {s}
              </Chip>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
